// frontend/src/pages/CustomerOrders.jsx

import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useTranslation } from 'react-i18next';
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import {
  Button,
} from "@mui/material";
import Tile from '../components/layout/Tile';
import { ItemFilterBar, useItemFilter } from '../features/item-filter';
import { useTabPersistedState } from '../tabs/useTabPersistedState';
import { useSsrmGrid, buildExportQuery, NULL_GROUP_KEY } from '../hooks/useSsrmGrid';
import { useQuery } from '@tanstack/react-query';
import { apiJson, translateRequestError } from '../queries/api';
import { customerOrderLines as customerOrderLinesKey } from '../queries/keys';
import { useAgGridTheme } from '../utils/agGridTheme';
import { fetchAllPages, REFERENCE_PAGE_SIZE } from '../utils/fetchAllPages';
import { defaultColDef, defaultGridProps, copyOnlyContextMenuItems, flexColumn } from '../utils/gridDefaults';
import { useColumnTypes } from '../hooks/useColumnTypes';
import { useGridState } from '../hooks/useGridState';
import { useAuth } from '../contexts/AuthContext';
import { useFormatters } from '../hooks/useFormatters';
import { buildPinnedTotalsRow, wrapColumnsForSummaryRows } from '../utils/pinnedTotals';
import DetailGrid from '../components/grid/DetailGrid';
import DetailRow from '../components/grid/DetailRow';
import ExpandAllButton from '../components/grid/ExpandAllButton';
import { useExpandAllRows } from '../hooks/useExpandAllRows';
import GridExportButton from '../components/common/GridExportButton';
import { useIsShopify, itemIdentityColumns } from '../components/grid/productIdentity';
import '../styles/pages.css';
import { GridEmptyState } from '../components/grid/NoRowsOverlay';
import { showToast } from '../components/common/appToastBus';
import { GridPage, GridViewport } from '../components/grid/GridPage';

// A line's net demand per location, as one line of text: "Polish Warehouse: 5 ·
// Dutch Warehouse: 10". A location with nothing left after returns is left out,
// so the cell only shows live demand; a dash when no location has any.
function locationNetDemandText(attributions, formatNumber) {
  const live = attributions
    .map((a) => ({ name: a.location_name, net: a.shipped_qty + a.open_qty - a.returned_qty }))
    .filter((a) => a.net > 0);
  return live.length ? live.map((a) => `${a.name}: ${formatNumber(a.net, 0)}`).join(' · ') : '-';
}

// Order-lines sub-grid, rendered through DetailGrid as a custom
// detailCellRenderer. Lines are fetched lazily on expand;
// staleTime 0 so re-expanding always shows current lines (webhooks can edit an
// order), while the shared key still dedupes concurrent fetches.
function CustomerOrderDetail({ data }) {
  const { t } = useTranslation();
  const isShopify = useIsShopify();
  const { formatNumber } = useFormatters();
  const { selectedStore } = useAuth();
  const currencySuffix = selectedStore?.currency ? ` (${selectedStore.currency})` : '';
  const orderId = data.customer_order_header_id;

  const { data: lines, isPending, isError, error } = useQuery({
    queryKey: customerOrderLinesKey.byOrder(selectedStore?.id, orderId),
    queryFn: () => fetchAllPages(
      (page) => apiJson.get(
        `/api/customer-order-lines/?CustomerOrderHeader=${orderId}&page=${page}&page_size=${REFERENCE_PAGE_SIZE}`,
      ),
      { label: 'customer order lines' },
    ),
    staleTime: 0,
    enabled: !!selectedStore?.id && !!orderId,
  });

  const columnDefs = useMemo(() => [
    ...itemIdentityColumns(isShopify, {
      productHeader: t('customerOrders.columns.product'),
      codeHeader: t('customerOrders.columns.itemCode'),
      skuHeader: t('customerOrders.columns.sku'),
      nameHeader: t('customerOrders.columns.itemName'),
    }),
    {
      field: "location_attributions",
      headerName: t("customerOrders.columns.location"),
      headerTooltip: t("customerOrders.tooltips.location"),
      valueFormatter: ({ value }) => locationNetDemandText(value, formatNumber),
    },
    {
      field: "Quantity",
      headerName: t("customerOrders.columns.quantity"),
      headerTooltip: t("customerOrders.tooltips.quantity"),
      type: 'numberColumn',
    },
    {
      field: "NetQuantity",
      headerName: t("customerOrders.columns.netQuantity"),
      headerTooltip: t("customerOrders.tooltips.netQuantity"),
      type: 'numberColumn',
    },
    {
      field: "Price",
      headerName: t("customerOrders.columns.price") + currencySuffix,
      type: 'decimalColumn',
      valueFormatter: (params) => formatNumber(params.value),
    },
    {
      headerName: t("customerOrders.columns.lineTotal") + currencySuffix,
      type: 'decimalColumn',
      valueGetter: (params) => Number(params.data.Quantity) * Number(params.data.Price),
      valueFormatter: (params) => formatNumber(params.value),
    },
    {
      field: "BookingTimeStamp",
      headerName: t("customerOrders.columns.bookingTimeStamp"),
      type: 'dateTimeColumn',
    },
    {
      field: "RequestedDate",
      headerName: t("customerOrders.columns.requestDeliveryDate"),
      headerTooltip: t("customerOrders.tooltips.requestDeliveryDate"),
      type: 'dateColumn',
    },
    {
      field: "ActualShipDate",
      headerName: t("customerOrders.columns.actualShippingDate"),
      type: 'dateColumn',
    },
    {
      field: "ShippingMethod",
      headerName: t("customerOrders.columns.shippingMethod"),
    },
    {
      field: "OrderLineStatus",
      headerName: t("customerOrders.columns.orderLineStatus"),
      // Empty until Shopify's fulfillment data has been read for the line,
      // and on lines of orders Shopify did not send.
      valueFormatter: (params) => params.value ? t(`customerOrders.lineStatus.${params.value}`) : '',
    },
  ], [t, isShopify, currencySuffix, formatNumber]);

  // Failed fetch must read as a failure, not as an order with no lines.
  if (isError) {
    return <DetailRow>{translateRequestError(error, t, 'common.fetchError')}</DetailRow>;
  }

  return (
    <DetailGrid
      rowData={lines}
      loading={isPending}
      columnDefs={columnDefs}
      stateKey="customer_orders_detail"
    />
  );
}


// Stable lists for useGridState's `alwaysHidden`, one per grouping mode.
const MONTH_GROUP_HIDDEN = ['order_month'];
const NO_HIDDEN = [];

function CustomerOrders() {
  const { t } = useTranslation();
  const gridTheme = useAgGridTheme();
  const columnTypes = useColumnTypes();
  const { formatNumber, formatMonthYear } = useFormatters();
  const { userContext, updatePreference } = useAuth();
  // Filtered-dataset totals (Subtotal / Tax / Total) — backend returns these
  // alongside count/results in every list response.
  const [totals, setTotals] = useState(null);
  const prefCustomerOrdersGrouped = !!userContext?.user?.ui_preferences?.customer_orders_grouped;
  const [isGrouped, setIsGrouped] = useState(prefCustomerOrdersGrouped);
  // Grouped, the month rows are groups and only the orders under them open.
  const isRowMaster = useCallback(
    (row) => (isGrouped ? row.data?.child_count == null : true),
    [isGrouped],
  );

  /* eslint-disable react-hooks/set-state-in-effect --
     Sync isGrouped with the persisted preference. The useState initializer
     captures userContext at first render only — if userContext isn't fully
     populated yet (race between AuthContext batching and child mount), the
     initializer sees false and the Group-by-Month toggle silently reverts on
     every refresh. Syncing here recovers once userContext lands. */
  useEffect(() => {
    setIsGrouped(prefCustomerOrdersGrouped);
  }, [prefCustomerOrdersGrouped]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // The month column is a column of the flat view and the group field of the
  // grouped one, so it is hidden only while grouped — and never as a merchant's
  // choice (hooks/useGridState.js `alwaysHidden`).
  const gridState = useGridState('customer_orders', undefined, {
    alwaysHidden: isGrouped ? MONTH_GROUP_HIDDEN : NO_HIDDEN,
  });

  const pageSize = 100;

  // Filter state — per-tab like every other list page (two CustomerOrders
  // tabs hold independent filters; survives F5).
  const [selectedLocation, setSelectedLocation] = useTabPersistedState('selectedLocation', 'all');
  const [selectedProductCategory, setSelectedProductCategory] = useTabPersistedState('selectedProductCategory', null);
  const itemFilter = useItemFilter();

  const gridApiRef = useRef(null);
  const expandAll = useExpandAllRows();

  // useCallback so dependent useCallbacks (datasource error handler, etc.)
  // don't churn on every render — lint rule `exhaustive-deps` flags the
  // unstable identity at every consumer otherwise.
  const createColumnDefs = useCallback(() => {
    const columns = [
      {
        field: "order_number",
        headerName: t("customerOrders.columns.orderNumber"),
        ...flexColumn(),
        cellRenderer: 'agGroupCellRenderer',
      },
      {
        field: "order_month",
        headerName: t("customerOrders.columns.orderMonth"),
        rowGroup: isGrouped,
        hide: isGrouped,
        // AG Grid takes a server-side group row's key from this column's
        // VALUE, and cannot open a group keyed null — so the orders without
        // a date are keyed by the sentinel the endpoint decodes
        // (components/grid/productGrouping.jsx keys its bucket the same way).
        valueGetter: ({ data, node }) => (
          node?.rowPinned || !data ? undefined : data.order_month ?? NULL_GROUP_KEY
        ),
        valueFormatter: (params) => {
          if (!params.value || params.value === NULL_GROUP_KEY) return t('customerOrders.noDate');
          // Backend returns a UTC ISO datetime for TruncMonth(order timestamp);
          // slice to 'YYYY-MM' rather than parsing a Date (see formatMonthYear
          // contract in utils/formatters.jsx).
          return formatMonthYear(String(params.value).slice(0, 7));
        },
      },
      // A month group row carries only its order count, no money
      // (api/views/customer_orders.py groups by order_month alone), so its
      // money cells stay empty rather than read as zero.
      {
        field: "subtotal",
        headerName: t("customerOrders.columns.subtotal"),
        type: 'decimalColumn',
        valueFormatter: (params) => (params.node?.group ? '' : formatNumber(params.value)),
      },
      {
        field: "total",
        headerName: t("customerOrders.columns.total"),
        type: 'decimalColumn',
        valueFormatter: (params) => (params.node?.group ? '' : formatNumber(params.value)),
      },
      {
        field: "currency_name",
        colId: "CurrencyCode__CurrencyName",
        headerName: t("customerOrders.columns.currency"),
      },
    ];

    return columns;
  }, [t, isGrouped, formatNumber, formatMonthYear]);

  // Column defs rebuild whenever createColumnDefs's deps change (incl. language via t/i18n.language).
  const columnDefs = useMemo(
    () => wrapColumnsForSummaryRows(createColumnDefs()),
    [createColumnDefs],
  );

  // Pinned-bottom totals row — sums subtotal/tax/total across the filtered dataset.
  // Label sits in the order_number column (first text column).
  const pinnedBottomRowData = useMemo(
    () => buildPinnedTotalsRow(totals, 'order_number', t('grid.totals.label')),
    [totals, t],
  );

  const ssrmParams = useMemo(() => ({
    ...itemFilter.params,
    product_category_id: selectedProductCategory?.id || null,
    location_code: selectedLocation === 'all' ? null : selectedLocation,
  }), [itemFilter.params, selectedProductCategory, selectedLocation]);

  // The filtered export covers exactly the rows the grid lists.
  const exportProps = {
    endpoint: '/api/customer-order-headers/export/',
    filename: 'customer_orders',
    getParams: (scope) => (scope === 'all' ? '' : buildExportQuery(ssrmParams)),
  };

  const handleSsrmData = useCallback((data) => {
    setTotals(data.totals);
  }, []);

  const handleSsrmError = useCallback((err) => {
    showToast(translateRequestError(err, t, 'customerOrders.messages.failedToLoad'), 'error');
  }, [t]);

  // Stable identity — see ForecastGrid's getRowId for the AG Grid 35.3 rationale.
  const getRowId = useCallback((params) => {
    if (params.data.child_count != null) {
      return `group_${params.data.order_month?.substring(0, 7) ?? NULL_GROUP_KEY}`;
    }
    return String(params.data.customer_order_header_id);
  }, []);

  const { onGridReady, onGridPreDestroyed, pagination } = useSsrmGrid({
    endpoint: '/api/customer-order-headers/',
    params: ssrmParams,
    pageSize,
    onData: handleSsrmData,
    onError: handleSsrmError,
    gridRef: gridApiRef,
  });

  const handleToggleGrouping = () => {
    setIsGrouped(prev => {
      const next = !prev;
      updatePreference('customer_orders_grouped', next);
      return next;
    });
  };

  return (
    <div className="page-container">
      <div className="tiles-row">
        <Tile
          width="full"
          title={t("customerOrders.title")}
          headerActions={<GridExportButton {...exportProps} />}
        >
          <GridPage>
            {/* Filters */}
            <ItemFilterBar
              filter={itemFilter}
              productCategoryFilter={{ selected: selectedProductCategory, set: setSelectedProductCategory, show: true }}
              locationFilter={{ selected: selectedLocation, set: setSelectedLocation }}
              actions={
                <Button
                  variant={isGrouped ? "contained" : "outlined"}
                  onClick={handleToggleGrouping}
                >
                  {t('customerOrders.groupByMonth')}
                </Button>
              }
            />
            <ExpandAllButton {...expandAll} />

            {/* Grid */}
            <GridViewport>
              {gridTheme && (
                <GridEmptyState
                  filtersActive={selectedLocation !== 'all' || !!selectedProductCategory || !!itemFilter.filter}
                >
                  <AgGridReact
                    {...defaultGridProps}
                    theme={gridTheme}
                    {...gridState}
                    columnDefs={columnDefs}
                    getRowId={getRowId}
                    defaultColDef={defaultColDef}
                    columnTypes={columnTypes}
                    autoGroupColumnDef={isGrouped ? {
                      headerName: t('customerOrders.columns.orderMonth'),
                      // Fixed width — no flex. The order number column is the
                      // grid's flex column; a second one here would split the
                      // leftover width between the two, which is not what a month
                      // group header needs.
                      width: 260,
                      minWidth: 200,
                      cellRendererParams: { suppressCount: false },
                      valueFormatter: (params) => {
                        // Leaf rows under an expanded group render this cell with
                        // no value — leave it blank rather than showing "No date",
                        // which is only meaningful on actual group header rows.
                        if (!params.node?.group) return '';
                        if (!params.value || params.value === NULL_GROUP_KEY) return t('customerOrders.noDate');
                        const [year, month] = params.value.split('-');
                        return formatMonthYear(`${year}-${String(month).padStart(2, '0')}`);
                      },
                    } : undefined}
                    groupHideOpenParents={isGrouped}
                    onGridReady={onGridReady}
                    onGridPreDestroyed={onGridPreDestroyed}
                    rowModelType="serverSide"
                    serverSideSortAllLevels={true}
                    {...pagination}
                    masterDetail={true}
                    detailCellRenderer={CustomerOrderDetail}
                    detailRowAutoHeight={true}
                    isRowMaster={isRowMaster}
                    {...expandAll.gridProps}
                    getContextMenuItems={copyOnlyContextMenuItems}
                    pinnedBottomRowData={pinnedBottomRowData}
                  />
                </GridEmptyState>
              )}
            </GridViewport>
          </GridPage>
        </Tile>
      </div>
    </div>
  );
}

export default CustomerOrders;